/**
 * SyncStatusBadge
 *
 * Live sync indicator + progress bar for page headers. Shows:
 *   • during sync     — "Syncing… N records" with a thin indeterminate bar
 *   • when up to date — "Synced · Xm ago"
 *   • offline         — "Offline"
 *   • on error        — "Sync issue · retry"
 *
 * Click triggers an immediate pull cycle.
 */

import React, { useEffect, useState } from "react";

import { usePullStatus } from "../hooks/usePullStatus";

interface BadgeStyle {
  classes: string;
  dot:     string;
  pulse?:  boolean;
}

function styleFor(state: string, isOnline: boolean): BadgeStyle {
  if (!isOnline || state === "offline") {
    return { classes: "bg-slate-100 text-slate-600 border-slate-300", dot: "bg-slate-400" };
  }
  switch (state) {
    case "bootstrapping":
      // Distinguished from "syncing" with a violet hue so the one-time initial
      // sync is visually obvious — useful during onboarding on slow networks.
      return { classes: "bg-violet-50 text-violet-700 border-violet-200", dot: "bg-violet-500", pulse: true };
    case "syncing":
      return { classes: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500", pulse: true };
    case "synced":
      return { classes: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" };
    case "error":
      return { classes: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" };
    default:
      return { classes: "bg-slate-50 text-slate-600 border-slate-200", dot: "bg-slate-400" };
  }
}

function formatAge(iso: string | null, now: number): string {
  if (!iso) return "never";
  const ageMs = now - new Date(iso).getTime();
  if (ageMs < 60_000)        return "just now";
  if (ageMs < 3_600_000)     return `${Math.floor(ageMs / 60_000)}m ago`;
  if (ageMs < 86_400_000)    return `${Math.floor(ageMs / 3_600_000)}h ago`;
  return `${Math.floor(ageMs / 86_400_000)}d ago`;
}

function labelFor(
  state: string,
  isOnline: boolean,
  recordsApplied: number,
  pagesDone: number,
  lastSyncedAt: string | null,
  now: number,
): string {
  if (!isOnline) return "Offline";
  switch (state) {
    case "bootstrapping":
      // The one-time initial sync. Use a different copy from "syncing" so a
      // user on a slow network understands this is expected to take longer.
      if (recordsApplied > 0) return `Initial sync… ${recordsApplied} record${recordsApplied === 1 ? "" : "s"}`;
      if (pagesDone      > 0) return `Initial sync… page ${pagesDone + 1}`;
      return "Initial sync…";
    case "syncing":
      // Prefer record count; fall back to page count on the very first request
      // before any apply has happened.
      if (recordsApplied > 0) return `Syncing… ${recordsApplied} record${recordsApplied === 1 ? "" : "s"}`;
      if (pagesDone      > 0) return `Syncing… page ${pagesDone + 1}`;
      return "Syncing…";
    case "synced":
      return `Synced · ${formatAge(lastSyncedAt, now)}`;
    case "error":
      return "Sync issue · retry";
    case "offline":
      return "Offline";
    default:
      return lastSyncedAt ? `Synced · ${formatAge(lastSyncedAt, now)}` : "Ready";
  }
}

interface SyncStatusBadgeProps {
  className?: string;
  /**
   * When false, render dot-only (no text label). Matches the collapsed-sidebar
   * affordance used by SyncErrorBadge. The title attribute still carries the
   * full label for hover tooltips. Defaults to true so existing call sites
   * (DashboardPage) keep their full-width rendering.
   */
  expanded?: boolean;
}

export const SyncStatusBadge: React.FC<SyncStatusBadgeProps> = ({ className = "", expanded = true }) => {
  const { state, isOnline, recordsApplied, pagesDone, lastSyncedAt, syncNow } = usePullStatus();

  // Re-render every 30s so "Synced · Nm ago" stays accurate without the hook
  // having to push fake events.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const s = styleFor(state, isOnline);
  const label = labelFor(state, isOnline, recordsApplied, pagesDone, lastSyncedAt, now);
  const disabled = !isOnline || state === "syncing" || state === "bootstrapping";
  const showBar = isOnline && (state === "syncing" || state === "bootstrapping");

  return (
    <div className={`relative inline-flex flex-col items-stretch ${className}`}>
      <button
        type="button"
        onClick={() => { void syncNow(); }}
        disabled={disabled}
        title={label}
        aria-label={label}
        className={`inline-flex items-center justify-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition disabled:cursor-not-allowed ${s.classes}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${s.dot} ${s.pulse ? "animate-pulse" : ""}`} />
        {expanded && label}
      </button>
      {showBar && (
        <div className="mt-1 h-0.5 w-full overflow-hidden rounded-full bg-blue-100" aria-hidden>
          <div className="h-full w-1/3 animate-[pull-bar_1.2s_ease-in-out_infinite] rounded-full bg-blue-500" />
        </div>
      )}
      {/* Indeterminate animation — keyframes inlined so the component is
          drop-in without touching the global Tailwind config. */}
      <style>{`
        @keyframes pull-bar {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(150%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
};

export default SyncStatusBadge;
